import { useState, useEffect, Fragment } from 'react'
import { Card, Badge, Loading } from '../components/common'
import { dashboardService } from '../services/dashboardService'
import {
  FileText, School, Factory, Package, Clock, CheckCircle, AlertTriangle,
  ChevronDown, ChevronRight, Printer, TrendingUp, Users, Layers, BookOpen, Scissors, Warehouse, Zap
} from 'lucide-react'

function ReportsPage() {
  const [activeTab, setActiveTab] = useState('spk-status')

  const tabs = [
    { id: 'spk-status', label: 'Status SPK', icon: FileText },
    { id: 'operators', label: 'Rekap Operator', icon: Users },
    { id: 'custom-schools', label: 'Sekolah Custom', icon: School },
    { id: 'vendors', label: 'Cetakan Vendor', icon: Factory },
    { id: 'comcolor', label: 'Cetakan Comcolor', icon: Zap },
    { id: 'lifecycle', label: 'Durasi SPK', icon: Clock },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">📊 Laporan</h1>
        <p className="text-gray-500">Laporan status produksi dan cetakan</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'spk-status' && <SPKStatusReport />}
      {activeTab === 'operators' && <OperatorReport />}
      {activeTab === 'custom-schools' && <CustomSchoolReport />}
      {activeTab === 'vendors' && <VendorReport />}
      {activeTab === 'comcolor' && <ComcolorReport />}
      {activeTab === 'lifecycle' && <LifecycleReport />}
    </div>
  )
}

// ==================== SPK STATUS REPORT ====================
function SPKStatusReport() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [expandedSection, setExpandedSection] = useState('pending')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await dashboardService.getSPKStatusReport()
      if (response.success) {
        setData(response.data)
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) return <Loading text="Memuat laporan..." />

  const { summary, pending = [], in_progress = [], completed = [] } = data || {}

  const stationLabels = {
    'validasi': 'Validasi File',
    'cetak_isi': 'Cetak Isi',
    'potong_isi': 'Potong Isi',
    'susun': 'Susun',
    'cetak_cover': 'Cetak Cover',
    'qc_cover': 'QC Cover',
    'laminasi': 'Laminasi',
    'cetak_sisipan': 'Cetak Sisipan',
    'qc_sisipan': 'QC Sisipan',
    'binding': 'Binding',
    'potong_sisir': 'Potong Sisir',
    'qc_potong': 'QC Potong',
    'gudang': 'Gudang',
    'done': 'Selesai'
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-200 rounded-full">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-orange-600">Belum Diproses</p>
              <p className="text-2xl font-bold text-orange-700">{summary?.pending || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-200 rounded-full">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-blue-600">Sedang Proses</p>
              <p className="text-2xl font-bold text-blue-700">{summary?.in_progress || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-200 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-green-600">Selesai</p>
              <p className="text-2xl font-bold text-green-700">{summary?.completed || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gray-200 rounded-full">
              <FileText className="w-6 h-6 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total SPK</p>
              <p className="text-2xl font-bold text-gray-700">{summary?.total || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Pending Section */}
      <CollapsibleSection
        title="Belum Diproses (Belum Ada Upload File)"
        count={pending.length}
        color="orange"
        isOpen={expandedSection === 'pending'}
        onToggle={() => setExpandedSection(expandedSection === 'pending' ? '' : 'pending')}
      >
        <SPKTable spks={pending} stationLabels={stationLabels} showFiles />
      </CollapsibleSection>

      {/* In Progress Section */}
      <CollapsibleSection
        title="Sedang Diproses"
        count={in_progress.length}
        color="blue"
        isOpen={expandedSection === 'in_progress'}
        onToggle={() => setExpandedSection(expandedSection === 'in_progress' ? '' : 'in_progress')}
      >
        <SPKTable spks={in_progress} stationLabels={stationLabels} showStations />
      </CollapsibleSection>

      {/* Completed Section */}
      <CollapsibleSection
        title="Selesai (Sudah di Gudang)"
        count={completed.length}
        color="green"
        isOpen={expandedSection === 'completed'}
        onToggle={() => setExpandedSection(expandedSection === 'completed' ? '' : 'completed')}
      >
        <SPKTable spks={completed} stationLabels={stationLabels} showWarehouse />
      </CollapsibleSection>
    </div>
  )
}

// ==================== OPERATOR REPORT ====================
function OperatorReport() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' })
  const [expandedStation, setExpandedStation] = useState(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const params = {}
      if (dateRange.startDate && dateRange.endDate) {
        params.startDate = dateRange.startDate
        params.endDate = dateRange.endDate
      }
      const response = await dashboardService.getOperatorReport(params)
      if (response.success) {
        setData(response.data)
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFilter = () => {
    fetchData()
  }

  const handleReset = () => {
    setDateRange({ startDate: '', endDate: '' })
    setTimeout(() => fetchData(), 0)
  }

  if (loading) return <Loading text="Memuat laporan operator..." />

  const { stations = [], summary = {} } = data || {}

  const stationIcons = {
    'susun': Layers,
    'laminasi': Package,
    'binding': BookOpen,
    'potong': Scissors,
    'gudang': Warehouse
  }

  const stationColors = {
    'susun': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'text-amber-600' },
    'laminasi': { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700', icon: 'text-pink-600' },
    'binding': { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: 'text-emerald-600' },
    'potong': { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', icon: 'text-cyan-600' },
    'gudang': { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', icon: 'text-purple-600' }
  }

  return (
    <div className="space-y-6">
      {/* Date Filter */}
      <Card>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal Mulai</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal Akhir</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <button
            onClick={handleFilter}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Filter
          </button>
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            Reset
          </button>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-200 rounded-full">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-blue-600">Total Operator</p>
              <p className="text-2xl font-bold text-blue-700">{summary.totalOperators || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-200 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-green-600">Total Inspeksi</p>
              <p className="text-2xl font-bold text-green-700">{(summary.totalInspeksi || 0).toLocaleString()}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-200 rounded-full">
              <Package className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-purple-600">Total Qty</p>
              <p className="text-2xl font-bold text-purple-700">{(summary.totalQty || 0).toLocaleString()}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-200 rounded-full">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-red-600">Total Reject</p>
              <p className="text-2xl font-bold text-red-700">{(summary.totalReject || 0).toLocaleString()}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Station Cards */}
      <div className="space-y-4">
        {stations.map(station => {
          const Icon = stationIcons[station.station] || Package
          const colors = stationColors[station.station] || stationColors['gudang']
          const isExpanded = expandedStation === station.station

          return (
            <div key={station.station} className={`rounded-lg border ${colors.border} overflow-hidden`}>
              <button
                onClick={() => setExpandedStation(isExpanded ? null : station.station)}
                className={`w-full p-4 flex items-center justify-between ${colors.bg} hover:opacity-90 transition`}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  <Icon className={`w-6 h-6 ${colors.icon}`} />
                  <div className="text-left">
                    <p className={`font-semibold ${colors.text}`}>{station.label}</p>
                    <p className="text-sm text-gray-500">{station.summary.totalOperators} operator</p>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <p className="font-bold text-gray-800">{station.summary.totalInspeksi.toLocaleString()}</p>
                    <p className="text-gray-500">Inspeksi</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-green-600">{station.summary.totalQty.toLocaleString()}</p>
                    <p className="text-gray-500">Qty</p>
                  </div>
                  {station.station !== 'gudang' && (
                    <div className="text-center">
                      <p className="font-bold text-red-600">{station.summary.totalReject.toLocaleString()}</p>
                      <p className="text-gray-500">Reject</p>
                    </div>
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="bg-white">
                  {station.operators.length === 0 ? (
                    <p className="p-4 text-center text-gray-500">Belum ada data operator</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Operator</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">
                            {station.station === 'gudang' ? 'Entry' : 'Inspeksi'}
                          </th>
                          {station.station !== 'gudang' && (
                            <>
                              <th className="px-4 py-3 text-right font-medium text-gray-600">Jml Awal</th>
                              <th className="px-4 py-3 text-right font-medium text-gray-600">Reject</th>
                              <th className="px-4 py-3 text-right font-medium text-gray-600">Jml Akhir</th>
                            </>
                          )}
                          {station.station === 'gudang' && (
                            <th className="px-4 py-3 text-right font-medium text-gray-600">Total Qty</th>
                          )}
                          <th className="px-4 py-3 text-right font-medium text-gray-600">SPK</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">Batch</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {station.operators.map((op, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-800">{op.name}</td>
                            <td className="px-4 py-3 text-right">
                              {station.station === 'gudang' ? op.totalEntry : op.totalInspeksi}
                            </td>
                            {station.station !== 'gudang' && (
                              <>
                                <td className="px-4 py-3 text-right">{op.totalJumlahAwal?.toLocaleString()}</td>
                                <td className="px-4 py-3 text-right text-red-600">{op.totalReject?.toLocaleString()}</td>
                                <td className="px-4 py-3 text-right text-green-600 font-medium">{op.totalJumlahAkhir?.toLocaleString()}</td>
                              </>
                            )}
                            {station.station === 'gudang' && (
                              <td className="px-4 py-3 text-right text-green-600 font-medium">{op.totalQty?.toLocaleString()}</td>
                            )}
                            <td className="px-4 py-3 text-right text-gray-600">{op.totalSPK}</td>
                            <td className="px-4 py-3 text-right text-gray-600">{op.totalBatch}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {stations.every(s => s.operators.length === 0) && (
        <Card>
          <div className="text-center py-8 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Belum ada data aktivitas operator</p>
          </div>
        </Card>
      )}
    </div>
  )
}

function CollapsibleSection({ title, count, color, isOpen, onToggle, children }) {
  const colors = {
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className={`w-full p-4 flex items-center justify-between ${colors[color]} border-b`}
      >
        <div className="flex items-center gap-3">
          {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          <span className="font-medium">{title}</span>
          <span className={`px-2 py-0.5 rounded-full text-sm bg-white`}>{count}</span>
        </div>
      </button>
      {isOpen && <div className="bg-white">{children}</div>}
    </div>
  )
}

function SPKTable({ spks, stationLabels, showFiles, showStations, showWarehouse }) {
  if (spks.length === 0) {
    return <p className="p-4 text-gray-500 text-center">Tidak ada data</p>
  }

  const renderTrackRow = (spk, track, isFirstRow) => {
    const isIsi = track === 'isi'
    const trackLabel = isIsi ? 'ISI' : 'COVER'
    const trackColor = isIsi
      ? 'bg-blue-100 text-blue-700 border-blue-200'
      : 'bg-rose-100 text-rose-700 border-rose-200'
    const station = isIsi ? spk.station_isi : spk.station_cover
    const progress = isIsi ? (spk.progress_isi || 0) : (spk.progress_cover || 0)
    const hasFile = isIsi ? spk.has_isi : spk.has_cover
    const stationLabel = station === 'done' ? 'Selesai' : (stationLabels[station] || station || '-')

    return (
      <tr key={`${spk.id}-${track}`} className={`hover:bg-gray-50 ${isFirstRow ? 'border-t-2 border-t-gray-200' : ''}`}>
        {isFirstRow && (
          <>
            <td className="px-4 py-3 align-top" rowSpan={2}>
              <a href={`/spk/${spk.id}`} className="font-medium text-blue-600 hover:underline">
                {spk.judul}
              </a>
              <div className="text-xs text-gray-500 font-mono mt-0.5">{spk.id}</div>
            </td>
            <td className="px-4 py-3 font-mono text-xs align-top" rowSpan={2}>{spk.kode}</td>
            <td className="px-4 py-3 align-top" rowSpan={2}>
              <Badge variant={spk.jenjang === 'SMP' ? 'primary' : 'default'}>{spk.jenjang}</Badge>
            </td>
            <td className="px-4 py-3 text-right align-top" rowSpan={2}>{Number(spk.qty || 0).toLocaleString()}</td>
          </>
        )}
        <td className="px-3 py-2">
          <span className={`text-xs px-2 py-0.5 rounded font-medium border ${trackColor}`}>
            {isIsi ? '📄 ' : '🎨 '}{trackLabel}
          </span>
        </td>
        {showFiles && (
          <td className="px-4 py-2 text-center">
            <span className={`text-xs px-1.5 py-0.5 rounded ${hasFile ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {hasFile ? '✓ Ada' : '✗ Belum'}
            </span>
          </td>
        )}
        {showStations && (
          <td className="px-4 py-2">
            {station && station !== 'done' ? (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{stationLabel}</span>
            ) : station === 'done' ? (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Selesai</span>
            ) : <span className="text-gray-400 text-xs">-</span>}
          </td>
        )}
        {showStations && (
          <td className="px-4 py-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    progress >= 100 ? 'bg-green-500' :
                    progress >= 50 ? 'bg-blue-500' : 'bg-orange-500'
                  }`}
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
              <span className={`text-xs font-medium min-w-[35px] text-right ${
                progress >= 100 ? 'text-green-600' :
                progress >= 50 ? 'text-blue-600' : 'text-orange-600'
              }`}>
                {progress}%
              </span>
            </div>
          </td>
        )}
        {isFirstRow && showWarehouse && (
          <td className="px-4 py-3 text-right align-top" rowSpan={2}>
            <span className={spk.warehouse_qty >= spk.qty ? 'text-green-600 font-medium' : ''}>
              {Number(spk.warehouse_qty || 0).toLocaleString()}
            </span>
          </td>
        )}
        {isFirstRow && (
          <td className="px-4 py-3 text-gray-500 align-top" rowSpan={2}>
            {spk.deadline ? new Date(spk.deadline).toLocaleDateString('id-ID') : '-'}
          </td>
        )}
      </tr>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left">SPK</th>
            <th className="px-4 py-3 text-left">Kode</th>
            <th className="px-4 py-3 text-left">Jenjang</th>
            <th className="px-4 py-3 text-right">Qty</th>
            <th className="px-3 py-3 text-left">Track</th>
            {showFiles && <th className="px-4 py-3 text-center">File</th>}
            {showStations && <th className="px-4 py-3 text-left">Stasiun Aktif</th>}
            {showStations && <th className="px-4 py-3 text-center" style={{width: '180px'}}>Progress</th>}
            {showWarehouse && <th className="px-4 py-3 text-right">Di Gudang</th>}
            <th className="px-4 py-3 text-left">Deadline</th>
          </tr>
        </thead>
        <tbody>
          {spks.map(spk => (
            <Fragment key={spk.id}>
              {renderTrackRow(spk, 'isi', true)}
              {renderTrackRow(spk, 'cover', false)}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ==================== CUSTOM SCHOOL REPORT ====================
function CustomSchoolReport() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [expandedSchool, setExpandedSchool] = useState(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await dashboardService.getCustomSchoolReport()
      if (response.success) {
        setData(response.data)
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) return <Loading text="Memuat laporan..." />

  const schools = data?.schools || []

  if (schools.length === 0) {
    return (
      <Card>
        <p className="text-center text-gray-500 py-8">Belum ada data sekolah custom</p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-orange-50 border-orange-200">
          <div className="text-center">
            <p className="text-3xl font-bold text-orange-600">
              {schools.reduce((sum, s) => sum + (Number(s.total_qty_pending) || 0), 0).toLocaleString()}
            </p>
            <p className="text-sm text-orange-600">Qty Belum Proses</p>
          </div>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600">
              {schools.reduce((sum, s) => sum + (Number(s.total_qty_in_progress) || 0), 0).toLocaleString()}
            </p>
            <p className="text-sm text-blue-600">Qty Sedang Proses</p>
          </div>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">
              {schools.reduce((sum, s) => sum + (Number(s.total_qty_completed) || 0), 0).toLocaleString()}
            </p>
            <p className="text-sm text-green-600">Qty Selesai</p>
          </div>
        </Card>
      </div>

      {/* School List */}
      <Card title="📚 Detail Per Sekolah">
        <div className="divide-y">
          {schools.map(school => (
            <div key={school.school_code}>
              <button
                onClick={() => setExpandedSchool(expandedSchool === school.school_code ? null : school.school_code)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  {expandedSchool === school.school_code ? 
                    <ChevronDown className="w-5 h-5 text-gray-400" /> : 
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  }
                  <div className="text-left">
                    <p className="font-medium">{school.school_code}</p>
                    <p className="text-sm text-gray-500">{school.school_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-lg font-bold text-orange-600">{Number(school.total_pending || 0)}</p>
                    <p className="text-xs text-gray-500">Pending</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-blue-600">{Number(school.total_in_progress || 0)}</p>
                    <p className="text-xs text-gray-500">Proses</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">{Number(school.total_completed || 0)}</p>
                    <p className="text-xs text-gray-500">Selesai</p>
                  </div>
                </div>
              </button>
              {expandedSchool === school.school_code && (
                <div className="bg-gray-50 p-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500">
                        <th className="text-left py-2">SPK</th>
                        <th className="text-left py-2">Tipe</th>
                        <th className="text-right py-2">Qty Order</th>
                        <th className="text-right py-2">Di Gudang</th>
                        <th className="text-center py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {school.spks.map((spk, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="py-2">
                            <a href={`/spk/${spk.spk_id}`} className="text-blue-600 hover:underline">
                              {spk.judul}
                            </a>
                          </td>
                          <td className="py-2">
                            <Badge variant={spk.custom_type === 'cover_only' ? 'default' : 'info'}>
                              {spk.custom_type === 'cover_only' ? 'Cover Only' : 'Cover + Sisipan'}
                            </Badge>
                          </td>
                          <td className="py-2 text-right">{spk.custom_qty?.toLocaleString()}</td>
                          <td className="py-2 text-right">{spk.warehouse_qty?.toLocaleString()}</td>
                          <td className="py-2 text-center">
                            <Badge variant={
                              spk.status === 'completed' ? 'success' : 
                              spk.status === 'in_progress' ? 'warning' : 'default'
                            }>
                              {spk.status === 'completed' ? 'Selesai' : 
                               spk.status === 'in_progress' ? 'Proses' : 'Pending'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

// ==================== VENDOR REPORT ====================
function VendorReport() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [expandedVendor, setExpandedVendor] = useState(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await dashboardService.getVendorReport()
      if (response.success) {
        setData(response.data)
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) return <Loading text="Memuat laporan..." />

  const vendors = data?.vendors || []

  if (vendors.length === 0) {
    return (
      <Card>
        <p className="text-center text-gray-500 py-8">Belum ada data vendor</p>
      </Card>
    )
  }

  // Calculate totals - ensure numbers
  const totals = vendors.reduce((acc, v) => ({
    total_qty: acc.total_qty + (Number(v.total_qty) || 0),
    qty_in_progress: acc.qty_in_progress + (Number(v.qty_in_progress) || 0),
    qty_completed: acc.qty_completed + (Number(v.qty_completed) || 0),
  }), { total_qty: 0, qty_in_progress: 0, qty_completed: 0 })

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-purple-50 border-purple-200">
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-600">{totals.total_qty.toLocaleString()}</p>
            <p className="text-sm text-purple-600">Total Qty Cetak</p>
          </div>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600">{totals.qty_in_progress.toLocaleString()}</p>
            <p className="text-sm text-blue-600">Qty Sedang Proses</p>
          </div>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">{totals.qty_completed.toLocaleString()}</p>
            <p className="text-sm text-green-600">Qty Selesai</p>
          </div>
        </Card>
      </div>

      {/* Vendor List */}
      <Card title="🏭 Detail Per Vendor">
        <div className="divide-y">
          {vendors.map(vendor => (
            <div key={vendor.vendor_id}>
              <button
                onClick={() => setExpandedVendor(expandedVendor === vendor.vendor_id ? null : vendor.vendor_id)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  {expandedVendor === vendor.vendor_id ? 
                    <ChevronDown className="w-5 h-5 text-gray-400" /> : 
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  }
                  <div className="text-left">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{vendor.vendor_name}</p>
                      <Badge variant={vendor.vendor_type === 'internal' ? 'default' : 'info'}>
                        {vendor.vendor_type}
                      </Badge>
                      {vendor.print_mode_group === 'offset' && (
                        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">🖨️ Offset</span>
                      )}
                      {vendor.print_mode_group === 'comcolor' && (
                        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">⚡ Comcolor</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{vendor.total_spk} SPK</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-lg font-bold text-purple-600">{Number(vendor.total_qty || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Qty Cetak</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-blue-600">{Number(vendor.qty_in_progress || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Proses</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">{Number(vendor.qty_completed || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Selesai</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-red-600">{Number(vendor.total_reject || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Reject</p>
                  </div>
                </div>
              </button>
              {expandedVendor === vendor.vendor_id && (
                <div className="bg-gray-50 p-4">
                  {vendor.jobs?.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-500">
                          <th className="text-left py-2">SPK</th>
                          <th className="text-left py-2">Kode</th>
                          <th className="text-left py-2">Tipe</th>
                          <th className="text-left py-2">Mode</th>
                          <th className="text-right py-2">Qty Cetak</th>
                          <th className="text-right py-2">Di Gudang</th>
                          <th className="text-right py-2">Reject</th>
                          <th className="text-center py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vendor.jobs.map((job, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="py-2">
                              <a href={`/spk/${job.spk_id}`} className="text-blue-600 hover:underline">
                                {job.judul}
                              </a>
                            </td>
                            <td className="py-2 font-mono text-xs">{job.kode}</td>
                            <td className="py-2">
                              <Badge variant={
                                job.track_type === 'isi' ? 'primary' :
                                job.track_type === 'cover' ? 'success' :
                                job.track_type === 'cover_custom' ? 'warning' : 'default'
                              }>
                                {job.track_type === 'isi' ? 'Isi' :
                                 job.track_type === 'cover' ? 'Cover' :
                                 job.track_type === 'cover_custom' ? 'Cover Custom' :
                                 job.track_type === 'sisipan' ? 'Sisipan' : job.track_type}
                              </Badge>
                            </td>
                            <td className="py-2">
                              {job.print_mode ? (
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                  job.print_mode === 'offset'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-emerald-100 text-emerald-700'
                                }`}>
                                  {job.print_mode === 'offset' ? '🖨️ Offset' : '⚡ Comcolor'}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                            <td className="py-2 text-right">{Number(job.qty || 0).toLocaleString()}</td>
                            <td className="py-2 text-right text-green-600">{Number(job.warehouse_qty || 0).toLocaleString()}</td>
                            <td className="py-2 text-right">
                              {Number(job.reject || 0) > 0 ? (
                                <span className="text-red-600 font-medium">{Number(job.reject).toLocaleString()}</span>
                              ) : '-'}
                            </td>
                            <td className="py-2 text-center">
                              <Badge variant={
                                job.status === 'completed' ? 'success' : 
                                job.status === 'in_progress' ? 'warning' : 'default'
                              }>
                                {job.status === 'completed' ? 'Selesai' : 
                                 job.status === 'in_progress' ? 'Proses' : 'Pending'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-gray-500 text-center py-4">Belum ada pekerjaan</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

// ==================== COMCOLOR REPORT ====================
function ComcolorReport() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [expandedSpk, setExpandedSpk] = useState(null)

  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await dashboardService.getComcolorReport()
      if (response.success) setData(response.data)
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) return <Loading text="Memuat laporan..." />

  const summary = data?.summary || {}
  const spks = data?.spks || []

  const trackLabel = (t) => ({
    isi: 'Isi', cover: 'Cover', cover_custom: 'Cover Custom', sisipan: 'Sisipan'
  }[t] || t)

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 border-emerald-200">
          <div className="text-center">
            <p className="text-3xl font-bold text-emerald-600">{summary.total_spk || 0}</p>
            <p className="text-sm text-emerald-600">Total SPK</p>
          </div>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-600">{(summary.total_qty || 0).toLocaleString()}</p>
            <p className="text-sm text-purple-600">Total Qty Cetak</p>
          </div>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600">{summary.in_progress || 0}</p>
            <p className="text-sm text-blue-600">SPK Proses</p>
          </div>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">{summary.completed || 0}</p>
            <p className="text-sm text-green-600">SPK Selesai</p>
          </div>
        </Card>
      </div>

      {spks.length === 0 ? (
        <Card>
          <div className="text-center py-12 text-gray-500">
            <Zap className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Belum ada SPK yang dicetak di Comcolor</p>
          </div>
        </Card>
      ) : (
        <Card title="⚡ Daftar SPK Cetakan Comcolor">
          <div className="divide-y">
            {spks.map(spk => (
              <div key={spk.spk_id}>
                <button
                  onClick={() => setExpandedSpk(expandedSpk === spk.spk_id ? null : spk.spk_id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    {expandedSpk === spk.spk_id
                      ? <ChevronDown className="w-5 h-5 text-gray-400" />
                      : <ChevronRight className="w-5 h-5 text-gray-400" />}
                    <div className="text-left">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-blue-600">{spk.judul}</p>
                        <span className="text-xs font-mono text-gray-500">{spk.kode}</span>
                        <Badge variant={spk.jenjang === 'SMP' ? 'primary' : 'default'}>{spk.jenjang}</Badge>
                        <Badge variant={
                          spk.spk_status === 'completed' ? 'success' :
                          spk.spk_status === 'in_progress' ? 'warning' : 'default'
                        }>
                          {spk.spk_status === 'completed' ? 'Selesai' :
                           spk.spk_status === 'in_progress' ? 'Proses' : 'Pending'}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {spk.spk_id} • Deadline: {spk.deadline ? new Date(spk.deadline).toLocaleDateString('id-ID') : '-'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-lg font-bold text-emerald-600">{Number(spk.total_comcolor_qty || 0).toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Qty Comcolor</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-gray-600">{Number(spk.spk_qty || 0).toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Qty SPK</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-green-600">{Number(spk.warehouse_qty || 0).toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Di Gudang</p>
                    </div>
                  </div>
                </button>
                {expandedSpk === spk.spk_id && (
                  <div className="bg-gray-50 p-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-500">
                          <th className="text-left py-2">Jenis File</th>
                          <th className="text-left py-2">Sekolah</th>
                          <th className="text-left py-2">Vendor</th>
                          <th className="text-right py-2">Qty</th>
                          <th className="text-left py-2">Tgl Mulai</th>
                          <th className="text-center py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {spk.jobs.map((job, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="py-2">
                              <Badge variant={
                                job.track_type === 'isi' ? 'primary' :
                                job.track_type === 'cover' ? 'success' :
                                job.track_type === 'cover_custom' ? 'warning' : 'default'
                              }>
                                📄 {trackLabel(job.track_type)}
                              </Badge>
                            </td>
                            <td className="py-2 text-xs font-mono">{job.school_code || '-'}</td>
                            <td className="py-2 text-xs">{job.vendor_name || 'Internal'}</td>
                            <td className="py-2 text-right font-medium">{Number(job.qty || 0).toLocaleString()}</td>
                            <td className="py-2 text-xs text-gray-600">
                              {job.start_date ? new Date(job.start_date).toLocaleDateString('id-ID') : '-'}
                            </td>
                            <td className="py-2 text-center">
                              <Badge variant={
                                job.status === 'completed' ? 'success' :
                                job.status === 'in_progress' ? 'warning' : 'default'
                              }>
                                {job.status === 'completed' ? 'Selesai' :
                                 job.status === 'in_progress' ? 'Proses' : 'Pending'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}

// ==================== LIFECYCLE / DURASI SPK REPORT ====================
// Threshold (jam) untuk klasifikasi lancar/sedang/macet
const LIFECYCLE_THRESHOLDS = {
  isi_revision: { ok: 48, warn: 120 },         // <2 hari ok, 2-5 hari warn, >5 hari macet
  cover_revision: { ok: 48, warn: 120 },
  isi_to_cover: { ok: 24, warn: 72 },          // <1 hari ok, 1-3 hari warn, >3 hari macet
  valid_to_cetak: { ok: 24, warn: 72 },
}

function LifecycleReport() {
  const [spks, setSpks] = useState([])
  const [loading, setLoading] = useState(true)
  const [sortBy, setSortBy] = useState('total_desc')

  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await dashboardService.getLifecycleReport()
      if (response.success) setSpks(response.data.spks || [])
    } catch (e) {
      console.error('Error:', e)
    } finally {
      setLoading(false)
    }
  }

  const formatHours = (h) => {
    if (h == null) return '-'
    if (h < 1) return `${Math.round(h * 60)} mnt`
    if (h < 24) return `${h.toFixed(1)} jam`
    const days = Math.floor(h / 24)
    const rem = Math.round(h % 24)
    return rem > 0 ? `${days}h ${rem}j` : `${days} hari`
  }

  const classify = (hours, key) => {
    if (hours == null) return 'none'
    const t = LIFECYCLE_THRESHOLDS[key]
    if (hours <= t.ok) return 'ok'
    if (hours <= t.warn) return 'warn'
    return 'bad'
  }

  const cellStyle = (status) => {
    if (status === 'ok') return 'bg-green-50 text-green-700 border-green-200'
    if (status === 'warn') return 'bg-amber-50 text-amber-700 border-amber-200'
    if (status === 'bad') return 'bg-red-50 text-red-700 border-red-200'
    return 'bg-gray-50 text-gray-400 border-gray-200'
  }

  const DurationCell = ({ hours, thresholdKey, versionCount }) => {
    const status = classify(hours, thresholdKey)
    return (
      <td className="px-3 py-2 text-center">
        <div className={`inline-block px-2 py-1 rounded border ${cellStyle(status)} text-xs font-medium min-w-[70px]`}>
          {formatHours(hours)}
        </div>
        {versionCount > 0 && (
          <div className="text-[10px] text-gray-500 mt-0.5">{versionCount} versi</div>
        )}
      </td>
    )
  }

  const printPathLabel = (p) => {
    if (!p) return <span className="text-gray-400 text-xs">-</span>
    const map = {
      offset_internal: { label: '🖨️ Offset Internal', cls: 'bg-blue-100 text-blue-700' },
      comcolor: { label: '⚡ Comcolor', cls: 'bg-emerald-100 text-emerald-700' },
      offset_external: { label: '🏭 Offset Ext', cls: 'bg-amber-100 text-amber-700' },
    }
    const x = map[p]
    return <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${x.cls}`}>{x.label}</span>
  }

  // Hitung skor total durasi per SPK untuk sort macet → lancar
  const totalDuration = (s) => {
    return (s.isi_revision_hours || 0)
      + (s.cover_revision_hours || 0)
      + (s.isi_valid_to_cover_upload_hours || 0)
      + (s.valid_to_cetak_hours || 0)
  }

  // Hitung skor "kemacetan": jumlah cell berstatus bad/warn
  const stuckScore = (s) => {
    const checks = [
      classify(s.isi_revision_hours, 'isi_revision'),
      classify(s.cover_revision_hours, 'cover_revision'),
      classify(s.isi_valid_to_cover_upload_hours, 'isi_to_cover'),
      classify(s.valid_to_cetak_hours, 'valid_to_cetak'),
    ]
    return checks.reduce((sum, x) => sum + (x === 'bad' ? 2 : x === 'warn' ? 1 : 0), 0)
  }

  if (loading) return <Loading text="Memuat laporan..." />

  const sorted = [...spks].sort((a, b) => {
    if (sortBy === 'stuck_desc') return stuckScore(b) - stuckScore(a)
    if (sortBy === 'total_desc') return totalDuration(b) - totalDuration(a)
    if (sortBy === 'total_asc') return totalDuration(a) - totalDuration(b)
    if (sortBy === 'deadline_asc') {
      const da = a.deadline ? new Date(a.deadline).getTime() : Infinity
      const db = b.deadline ? new Date(b.deadline).getTime() : Infinity
      return da - db
    }
    return 0
  })

  // Hitung summary
  const stuckCount = spks.filter(s => stuckScore(s) >= 3).length
  const warningCount = spks.filter(s => stuckScore(s) >= 1 && stuckScore(s) < 3).length
  const smoothCount = spks.length - stuckCount - warningCount
  const avgValidToCetak = (() => {
    const vals = spks.map(s => s.valid_to_cetak_hours).filter(v => v != null)
    if (vals.length === 0) return null
    return vals.reduce((a, b) => a + b, 0) / vals.length
  })()

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gray-50 border-gray-200">
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-700">{spks.length}</p>
            <p className="text-sm text-gray-600">Total SPK</p>
          </div>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">{smoothCount}</p>
            <p className="text-sm text-green-600">🟢 Lancar</p>
          </div>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <div className="text-center">
            <p className="text-3xl font-bold text-amber-600">{warningCount}</p>
            <p className="text-sm text-amber-600">🟡 Sedang</p>
          </div>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <div className="text-center">
            <p className="text-3xl font-bold text-red-600">{stuckCount}</p>
            <p className="text-sm text-red-600">🔴 Macet</p>
          </div>
        </Card>
      </div>

      {avgValidToCetak != null && (
        <Card className="bg-blue-50 border-blue-200">
          <div className="text-center text-sm text-blue-700">
            ⏱ Rata-rata jeda dari File Valid → Mulai Cetak: <strong>{formatHours(avgValidToCetak)}</strong>
          </div>
        </Card>
      )}

      {spks.length === 0 ? (
        <Card>
          <div className="text-center py-12 text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Belum ada data SPK</p>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h3 className="font-bold text-gray-800">⏱️ Durasi Tahapan Setiap SPK</h3>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500">Urutkan:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-2 py-1 border rounded text-sm"
              >
                <option value="stuck_desc">Paling Macet Dulu</option>
                <option value="total_desc">Total Durasi Terlama</option>
                <option value="total_asc">Total Durasi Tercepat</option>
                <option value="deadline_asc">Deadline Terdekat</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs text-gray-600">
                  <th className="px-3 py-2">SPK</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="px-3 py-2 text-center">Lama Revisi ISI</th>
                  <th className="px-3 py-2 text-center">ISI Valid → Cover Upload</th>
                  <th className="px-3 py-2 text-center">Lama Revisi Cover</th>
                  <th className="px-3 py-2 text-center">Valid → Cetak</th>
                  <th className="px-3 py-2 text-center">Jalur Cetak</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sorted.map(s => {
                  const score = stuckScore(s)
                  const statusBadge = score >= 3
                    ? { label: '🔴 Macet', cls: 'bg-red-100 text-red-700' }
                    : score >= 1
                    ? { label: '🟡 Sedang', cls: 'bg-amber-100 text-amber-700' }
                    : { label: '🟢 Lancar', cls: 'bg-green-100 text-green-700' }
                  return (
                    <tr key={s.spk_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <a href={`/spk/${s.spk_id}`} className="font-medium text-blue-600 hover:underline">
                          {s.judul}
                        </a>
                        <div className="text-xs text-gray-500 font-mono">{s.spk_id} • {s.kode}</div>
                        {s.deadline && (
                          <div className="text-[10px] text-gray-500 mt-0.5">
                            DL: {new Date(s.deadline).toLocaleDateString('id-ID')}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${statusBadge.cls}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                      <DurationCell hours={s.isi_revision_hours} thresholdKey="isi_revision" versionCount={s.isi_version_count} />
                      <DurationCell hours={s.isi_valid_to_cover_upload_hours} thresholdKey="isi_to_cover" />
                      <DurationCell hours={s.cover_revision_hours} thresholdKey="cover_revision" versionCount={s.cover_version_count} />
                      <DurationCell hours={s.valid_to_cetak_hours} thresholdKey="valid_to_cetak" />
                      <td className="px-3 py-2 text-center">{printPathLabel(s.print_path)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 text-xs text-gray-500 space-y-0.5 border-t pt-3">
            <p className="font-medium text-gray-600 mb-1">Legenda warna:</p>
            <div className="flex flex-wrap gap-3">
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-200 border border-green-300 rounded"></span> Lancar</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-200 border border-amber-300 rounded"></span> Sedang</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-200 border border-red-300 rounded"></span> Macet</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gray-100 border border-gray-300 rounded"></span> Belum ada data</span>
            </div>
            <p className="mt-2">• <strong>Lama Revisi</strong> {`<48j = lancar, 48–120j = sedang, >120j (5 hari) = macet`}</p>
            <p>• <strong>Jeda antar tahap</strong> {`<24j = lancar, 24–72j = sedang, >72j (3 hari) = macet`}</p>
          </div>
        </Card>
      )}
    </div>
  )
}

export default ReportsPage
